import type {
  AuthService,
  AuthUser,
  InitiatePhoneAuthResult,
  PhoneConfirmationResult,
  RecaptchaVerifierInstance,
  UserCredential,
} from "@infrastructure/interfaces/database.interface";
import { createBackendRegistry } from "@infrastructure/providers/backendRegistry";

const { auth } = createBackendRegistry();

/**
 * Authentication service for phone-based authentication operations.
 *
 * Provides a unified interface for phone authentication including SMS verification,
 * code confirmation, and session management using the backend registry pattern.
 *
 * @example
 * ```typescript
 * import authService from '@features/authentication/services/authService';
 *
 * // Create reCAPTCHA verifier
 * const recaptchaVerifier = authService.createRecaptchaVerifier('recaptcha-container');
 *
 * // Send verification code
 * const confirmationResult = await authService.sendVerificationCode('+61412345678', recaptchaVerifier);
 *
 * // Confirm the code
 * const userCredential = await authService.confirmVerificationCode(confirmationResult, '123456');
 *
 * // Subscribe to auth state changes
 * const unsubscribe = authService.onAuthStateChanged((user) => {
 *   if (user) {
 *     console.log('User signed in:', user.uid);
 *   }
 * });
 * ```
 */
const authService: AuthService = {
  /**
   * Sends an SMS verification code to the provided phone number.
   * @param phoneNumber - Phone number in E.164 format (e.g., +61412345678)
   * @param recaptchaVerifier - reCAPTCHA verifier instance for abuse prevention
   */
  async sendVerificationCode(
    phoneNumber: string,
    recaptchaVerifier: RecaptchaVerifierInstance
  ): Promise<PhoneConfirmationResult> {
    return await auth.sendVerificationCode(phoneNumber, recaptchaVerifier);
  },

  /**
   * Confirms the SMS verification code and completes authentication.
   * Creates a new user document if this is a new user.
   * @param confirmationResult - The confirmation result from sendVerificationCode
   * @param verificationCode - The 6-digit SMS code entered by the user
   */
  async confirmVerificationCode(
    confirmationResult: PhoneConfirmationResult,
    verificationCode: string
  ): Promise<UserCredential> {
    return await auth.confirmVerificationCode(confirmationResult, verificationCode);
  },

  async signOut(): Promise<void> {
    return await auth.signOut();
  },

  getCurrentAuthUser(): AuthUser | null {
    return auth.getCurrentAuthUser();
  },

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return auth.onAuthStateChanged(callback);
  },

  /**
   * Creates a reCAPTCHA verifier for phone authentication.
   * @param containerId - DOM element ID for the reCAPTCHA container
   * @param invisible - Whether to use invisible reCAPTCHA (default: true)
   */
  createRecaptchaVerifier(containerId: string, invisible: boolean = true): RecaptchaVerifierInstance {
    return auth.createRecaptchaVerifier(containerId, invisible);
  },

  /**
   * Checks whether a phone number belongs to a new or returning user.
   * New users receive a custom token for instant sign-in (no OTP).
   * @param phoneNumber - Phone number in E.164 format
   */
  async initiatePhoneAuth(phoneNumber: string): Promise<InitiatePhoneAuthResult> {
    return await auth.initiatePhoneAuth(phoneNumber);
  },

  /**
   * Signs in with a server-issued custom token (used for new-user instant sign-in).
   * @param token - Custom token from the initiatePhoneAuth callable
   */
  async signInWithCustomToken(token: string): Promise<void> {
    return await auth.signInWithCustomToken(token);
  },
};

export default authService;
